// roundRobinScheduler.js
// Implementazione dell'algoritmo "round-robin" con metodo della rondella
// per generare incontri senza che gli atleti facciano due assalti consecutivi

/**
 * Sequenze standard FIE ufficiali per gironi da 4 a 8 tiratori
 * Queste sequenze garantiscono che nessun tiratore faccia due assalti consecutivi
 */
const STANDARD_SEQUENCES = {
  // 4 Tiratori (6 assalti)
  4: [
    [0, 3], [1, 2], [0, 2], [1, 3], [0, 1], [2, 3]
  ],
  // 5 Tiratori (10 assalti)
  5: [
    [0, 1], [2, 3], [4, 0], [1, 2], [4, 3],
    [0, 2], [1, 4], [3, 0], [2, 4], [3, 1]
  ],
  // 6 Tiratori (15 assalti)
  6: [
    [0, 1], [3, 4], [1, 2], [4, 5], [2, 0],
    [5, 3], [1, 4], [0, 3], [4, 2], [0, 5],
    [3, 1], [2, 5], [4, 0], [2, 3], [5, 1]
  ],
  // 7 Tiratori (21 assalti)
  7: [
    [0, 3], [1, 4], [2, 5], [6, 0], [4, 3],
    [1, 2], [5, 6], [4, 0], [3, 2], [5, 1],
    [4, 6], [2, 0], [3, 5], [6, 1], [2, 4],
    [0, 5], [1, 3], [6, 2], [5, 4], [0, 1],
    [3, 6]
  ],
  // 8 Tiratori (28 assalti)
  8: [
    [1, 2], [0, 4], [6, 3], [5, 7], [0, 1],
    [2, 3], [4, 5], [7, 6], [3, 0], [4, 1],
    [7, 2], [5, 6], [3, 1], [7, 0], [6, 4],
    [2, 5], [1, 7], [4, 3], [5, 0], [2, 6],
    [3, 7], [1, 5], [2, 4], [0, 6], [3, 5],
    [7, 4], [6, 1], [0, 2]
  ]
};

/**
 * Genera i turni round-robin per N atleti (numerati da 0 a N-1).
 * Se N è dispari, aggiunge un "ghost" per i bye.
 * Restituisce una lista di turni, ciascun turno è una lista di coppie [a,b].
 * @param {number} n - Numero di atleti
 * @returns {Array<Array<[number,number]>>} - Lista di turni, ogni turno contiene coppie di atleti
 */
export function generateRounds(n) {
  // Crea la lista di atleti 0..n-1, e aggiunge ghost=-1 se dispari
  const athletes = Array.from({ length: n }, (_, i) => i);
  const ghost = -1;

  if (n % 2 === 1) {
    athletes.push(ghost);
  }

  const R = athletes.length - 1;
  const mid = athletes.length / 2;

  const rounds = [];
  for (let r = 0; r < R; r++) {
    const pairs = [];
    for (let i = 0; i < mid; i++) {
      const a = athletes[i];
      const b = athletes[athletes.length - 1 - i];
      // escludi i bye (ghost)
      if (a !== ghost && b !== ghost) {
        pairs.push([a, b]);
      }
    }
    rounds.push(pairs);

    // rotazione: fisso athletes[0], ruoto il resto verso destra
    const rotated = [athletes[0], athletes[athletes.length - 1]];
    for (let i = 1; i < athletes.length - 1; i++) {
      rotated.push(athletes[i]);
    }
    athletes.splice(0, athletes.length, ...rotated);
  }

  return rounds;
}

/**
 * "Appiattisce" i turni in un solo elenco di match,
 * preservando l'ordine dei turni.
 * @param {Array<Array<[number,number]>>} rounds - Lista di turni
 * @returns {Array<[number,number]>} - Lista piatta di tutte le partite
 */
export function flattenSchedule(rounds) {
  return rounds.flatMap(round => round);
}

/**
 * Genera la sequenza di assalti per due squadre utilizzando l'algoritmo round-robin
 * con il metodo della rondella, che assicura che nessun atleta faccia due assalti consecutivi.
 * @param {Array} redTeam - Array dei nomi della squadra rossa
 * @param {Array} greenTeam - Array dei nomi della squadra verde
 * @returns {Array<{fencerA: string, fencerB: string}>} - Lista di assalti
 */
export function generateRoundRobinAssalti(redTeam, greenTeam) {
  // Se una delle squadre è vuota, restituisci un array vuoto
  if (!redTeam || !greenTeam || redTeam.length === 0 || greenTeam.length === 0) {
    return [];
  }

  // Nel caso in cui abbiamo una sola lista di atleti (tutti contro tutti)
  if (redTeam === greenTeam || (redTeam.length === greenTeam.length && redTeam.every((item, index) => item === greenTeam[index]))) {
    return generateCompleteRoundRobin(redTeam);
  }

  // Converte i nomi in oggetti se necessario
  const redNames = redTeam.map(name => typeof name === 'object' ? name : { name });
  const greenNames = greenTeam.map(name => typeof name === 'object' ? name : { name });

  // Genera gli incontri con l'algoritmo round-robin
  const rounds = generateRounds(redNames.length + greenNames.length);
  const schedule = flattenSchedule(rounds);

  // Crea un array combinato di entrambe le squadre
  // allFencers rimosso (inutilizzato)

  // Filtra solo gli incontri tra squadre diverse (rosso vs verde)
  const crossTeamMatches = schedule.filter(([a, b]) => {
    const isAFromRed = a < redNames.length;
    const isBFromRed = b < redNames.length;
    return isAFromRed !== isBFromRed; // Uno deve essere rosso e l'altro verde
  });

  // Crea gli oggetti assalto nel formato richiesto
  return crossTeamMatches.map(([a, b]) => {
    // Assicuriamoci che fencerA sia sempre dalla squadra rossa
    if (a < redNames.length) {
      return {
        fencerA: redNames[a],
        fencerB: greenNames[b - redNames.length]
      };
    } else {
      return {
        fencerA: redNames[b],
        fencerB: greenNames[a - redNames.length]
      };
    }
  });
}

/**
 * Genera un girone all'italiana completo (tutti contro tutti) seguendo le sequenze
 * standard ufficiali per garantire che nessun atleta faccia due assalti consecutivi.
 * @param {Array} participants - Array dei nomi dei partecipanti
 * @returns {Array<{fencerA: string, fencerB: string}>} - Lista di assalti
 */
export function generateCompleteRoundRobin(participants) {
  // Se ci sono meno di 2 partecipanti, non ci sono incontri
  if (!participants || participants.length < 2) {
    return [];
  }

  // Converte i nomi in oggetti se necessario
  const fencers = participants.map(name => typeof name === 'object' ? name : { name });

  // Numero totale di partecipanti
  const n = fencers.length;

  // Per un round robin completo, ogni atleta deve affrontare tutti gli altri una volta
  // Quindi il numero totale di incontri è n(n-1)/2
  const totalMatches = (n * (n - 1)) / 2;

  // Usa la sequenza standard se disponibile
  let matches = [];

  if (STANDARD_SEQUENCES[n]) {
    // Usiamo la sequenza predefinita
    matches = STANDARD_SEQUENCES[n];
    console.log(`Utilizzo sequenza standard per ${n} tiratori (${matches.length} assalti)`);
  } else {
    // Genera i turni con il metodo della rondella se non c'è una sequenza standard
    const rounds = generateRounds(n);
    matches = flattenSchedule(rounds);
    console.log(`Utilizzo sequenza generata per ${n} tiratori (${matches.length} assalti)`);
  }

  // Assicurarsi che abbiamo generato abbastanza incontri
  if (matches.length < totalMatches) {
    console.error(`Errore nella generazione: generati ${matches.length} assalti invece di ${totalMatches} attesi`);
  }

  // Crea gli oggetti assalto nel formato richiesto
  return matches.map(([a, b]) => ({
    fencerA: fencers[a],
    fencerB: fencers[b]
  }));
}
